import React, { useEffect } from "react";
import { Select } from "@telia/teddy";
import { wouldGetFamilyDiscount } from "./familyDiscount";
import { getSubscriptionOptions, ExistingForChangeDisplay } from "./utils";
import { RDSubscription } from "../../../../../../models";
import { getDiscountedMonthlyPrice } from "../../../../../../shared/discounts";

const FamilyDiscountLabel: React.FC<{ sub: RDSubscription; offeringCodes: string[] }> = ({ sub, offeringCodes }) => {
    if (!wouldGetFamilyDiscount(sub.offeringCode, offeringCodes)) return null;
    if (!sub.monthlyPriceLines || sub.monthlyPrice <= sub.monthlyPriceLines) return null;
    return (
        <span className="choose-subscription__family-discount">
            (familierabatt: -{sub.monthlyPrice - sub.monthlyPriceLines},-/md)
        </span>
    );
};

const PriceLabel: React.FC<{ displayedMonthlyPrice: number | undefined }> = ({ displayedMonthlyPrice }) => {
    if (!displayedMonthlyPrice) return null;
    return <>{` - ${displayedMonthlyPrice},-/md`}</>;
};

const SubscriptionItemLabel: React.FC<{
    sub: RDSubscription;
    existingForChangeDisplay: ExistingForChangeDisplay | undefined;
    offeringCodes: string[];
}> = ({ sub, existingForChangeDisplay, offeringCodes }) => {
    const displayedMonthlyPrice = getDiscountedMonthlyPrice(
        sub.monthlyPrice,
        sub.monthlyDiscount,
        sub.firstYearDiscountAmount,
    );

    if (existingForChangeDisplay?.code.split(".")[0] === `${sub.offeringCode}`) {
        return (
            <span>
                Beholder eksisterende: {sub.subscriptionName}
                <FamilyDiscountLabel sub={sub} offeringCodes={offeringCodes} />
            </span>
        );
    }
    return (
        <span>
            {sub.subscriptionName}
            <PriceLabel displayedMonthlyPrice={displayedMonthlyPrice} />
            <FamilyDiscountLabel sub={sub} offeringCodes={offeringCodes} />
        </span>
    );
};

export interface SubscriptionSelectProps {
    name: string;
    subscriptions: RDSubscription[];
    selectedOfferingCode: string | undefined;
    existingForChangeDisplay: ExistingForChangeDisplay | undefined;
    offeringCodes: string[];
    onValueChange: (value: string) => void;
    // Whether this select's tab/category is the one currently shown. Radix keeps the
    // previous Tabs.Content briefly mounted during the switch, so without this guard its
    // auto-select effect can clobber selectedOfferingCode with the wrong tab's value.
    isActive?: boolean;
}

const SubscriptionSelect: React.FC<SubscriptionSelectProps> = ({
    name,
    subscriptions,
    selectedOfferingCode,
    existingForChangeDisplay,
    offeringCodes,
    onValueChange,
    isActive = true,
}) => {
    const hasOnlyOneSubscription = subscriptions.length === 1;

    // When there's only one subscription, use it as default value
    const defaultValue =
        hasOnlyOneSubscription && subscriptions.length > 0
            ? `${subscriptions[0].offeringCode}.${subscriptions[0].icim}`
            : "";

    // Automatically select the single subscription immediately
    useEffect(() => {
        if (isActive && hasOnlyOneSubscription && defaultValue && selectedOfferingCode !== defaultValue) {
            onValueChange(defaultValue);
        }
    }, [isActive, hasOnlyOneSubscription, defaultValue, selectedOfferingCode, onValueChange]);

    // Determine the current value to display
    const currentValue = hasOnlyOneSubscription ? defaultValue : selectedOfferingCode || "";

    return (
        <Select
            className="choose-subscription__select"
            label={hasOnlyOneSubscription ? undefined : "Velg abonnement"}
            name={name}
            options={getSubscriptionOptions(subscriptions, existingForChangeDisplay)}
            value={currentValue}
            onValueChange={(value: string) => {
                onValueChange(value);
                return value;
            }}
        >
            <Select.Trigger className="choose-subscription__select-trigger">
                <Select.Value placeholder={hasOnlyOneSubscription ? "" : "Velge..."} />
            </Select.Trigger>
            <Select.Portal>
                <Select.Content>
                    <Select.Viewport>
                        {subscriptions.map((sub) => (
                            <Select.Item
                                key={`${sub.offeringCode}.${sub.icim}`}
                                value={`${sub.offeringCode}.${sub.icim}`}
                            >
                                <SubscriptionItemLabel
                                    sub={sub}
                                    existingForChangeDisplay={existingForChangeDisplay}
                                    offeringCodes={offeringCodes}
                                />
                            </Select.Item>
                        ))}
                    </Select.Viewport>
                </Select.Content>
            </Select.Portal>
        </Select>
    );
};

export default SubscriptionSelect;
